import React from 'react'
import PropTypes from 'prop-types'
import { Box, Chip, Typography } from '@mui/material'
import { ThumbUp as ThumbUpIcon, ThumbDown as ThumbDownIcon } from '@mui/icons-material'

const matchTierColors = {
  Excellent: { bg: '#e8f5e9', text: '#2e7d32' },
  Strong: { bg: '#e3f2fd', text: '#1565c0' },
  Good: { bg: '#fff3e0', text: '#e65100' },
  Moderate: { bg: '#f5f5f5', text: '#616161' },
}

const JobCard = ({
  rank,
  matchTier,
  justification,
  title,
  employer,
  city,
  state,
  practiceType,
  feedbackVote,
}) => {
  const tierColor = matchTierColors[matchTier] || matchTierColors.Moderate
  const location = [city, state].filter(Boolean).join(', ')

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 1.5,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant='caption' color='textSecondary' sx={{ fontWeight: 600, minWidth: 16 }}>
          #{rank}
        </Typography>
        <Chip
          label={matchTier}
          size='small'
          sx={{
            height: 18,
            fontSize: '0.65rem',
            bgcolor: tierColor.bg,
            color: tierColor.text,
            fontWeight: 600,
          }}
        />
        {practiceType && (
          <Chip
            label={practiceType}
            size='small'
            variant='outlined'
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        )}
      </Box>

      <Typography variant='subtitle2' sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {title || 'Untitled Job'}
      </Typography>

      {(employer || location) && (
        <Typography variant='caption' color='textSecondary'>
          {[employer, location].filter(Boolean).join(' - ')}
        </Typography>
      )}

      {justification && (
        <Typography
          variant='body2'
          color='textSecondary'
          sx={{
            fontStyle: 'italic',
            fontSize: '0.75rem',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {justification}
        </Typography>
      )}

      {feedbackVote != null && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: feedbackVote === 1 ? '#e8f5e9' : '#fbe9e7',
            width: 'fit-content',
          }}
        >
          {feedbackVote === 1 ? (
            <ThumbUpIcon sx={{ fontSize: 12, color: '#2e7d32' }} />
          ) : (
            <ThumbDownIcon sx={{ fontSize: 12, color: '#c62828' }} />
          )}
          <Typography variant='caption' sx={{ fontSize: '0.65rem', fontWeight: 600, color: feedbackVote === 1 ? '#2e7d32' : '#c62828' }}>
            {feedbackVote === 1 ? 'User rated positively' : 'User rated negatively'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

JobCard.propTypes = {
  rank: PropTypes.number.isRequired,
  matchTier: PropTypes.string,
  justification: PropTypes.string,
  title: PropTypes.string,
  employer: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  practiceType: PropTypes.string,
  feedbackVote: PropTypes.number,
}

export default JobCard
